'use client'
import {
  useUserAddVideoKycDocument,
  useUserStartVideoKyc,
} from '@repo/services'
import { toast } from '@repo/ui/components'
import { useCallback, useEffect, useRef, useState } from 'react'
import 'webrtc-adapter'
import { SelfieKycConfirm } from './selfie-kyc.confirm'
import { SelfieKycRecording } from './selfie-kyc.recording'
import { SelfieKycStart } from './selfie-kyc.start'
import {
  ISelfieKycContainerProps,
  ISelfieKycState,
  SelfieKycStep,
} from './selfie-kyc.types'
import { useLanguage, useTranslate } from '@repo/shared/hooks'

const compressVideo = async (blob: Blob): Promise<Blob> => {
  const MAX_SIZE = 1024 * 1024 // 1MB in bytes
  const originalSize = blob.size

  if (originalSize <= MAX_SIZE) {
    return blob
  }

  return new Promise((resolve) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!

    video.onloadedmetadata = () => {
      // Calculate new dimensions to reduce file size
      const newWidth = video.videoWidth
      const newHeight = video.videoHeight

      // Reduce quality progressively until size is under 1MB
      let quality = 0.8

      const compress = () => {
        canvas.width = newWidth
        canvas.height = newHeight

        ctx.drawImage(video, 0, 0, newWidth, newHeight)

        canvas.toBlob(
          (compressedBlob) => {
            if (compressedBlob) {
              if (compressedBlob.size <= MAX_SIZE || quality <= 0.1) {
                resolve(compressedBlob)
              } else {
                // Reduce quality and try again
                quality -= 0.1
                compress()
              }
            } else {
              resolve(blob)
            }
          },
          'video/webm',
          quality,
        )
      }

      video.oncanplay = () => {
        compress()
      }

      video.src = URL.createObjectURL(blob)
    }

    video.load()
  })
}

// Check permissions by actually trying to access media devices
const checkMediaPermission = async (): Promise<boolean> => {
  try {
    if (!navigator.mediaDevices?.getUserMedia) return false
    const stream = await navigator.mediaDevices.getUserMedia({ video: true })
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch {
    return false
  }
}

const checkMicrophonePermission = async (): Promise<boolean> => {
  try {
    if (!navigator.mediaDevices?.getUserMedia) return false
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch {
    return false
  }
}

export const SelfieKycContainer = (props: ISelfieKycContainerProps) => {
  const [state, setState] = useState<ISelfieKycState>({
    step: SelfieKycStep.Start,
    videoBlob: undefined,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [hasCameraPermission, setHasCameraPermission] = useState(false)
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState(false)
  const [isRequestingAccess, setIsRequestingAccess] = useState(false)
  const language = useLanguage()
  const { t } = useTranslate()
  const activeStreamRef = useRef<MediaStream | null>(null)

  // Function to check and update permissions
  const checkAndUpdatePermissions = useCallback(async () => {
    try {
      const cameraGranted = await checkMediaPermission()
      const microphoneGranted = await checkMicrophonePermission()
      setHasCameraPermission(cameraGranted)
      setHasMicrophonePermission(microphoneGranted)
    } catch (error) {
      console.error('Error checking permissions:', error)
      setHasCameraPermission(false)
      setHasMicrophonePermission(false)
    }
  }, [])

  // Check permissions on component mount
  useEffect(() => {
    checkAndUpdatePermissions()

    // Cleanup any active stream on unmount
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => {
          track.stop()
          track.enabled = false
        })
        activeStreamRef.current = null
      }
    }
  }, [checkAndUpdatePermissions])

  // Video KYC mutations
  const startVideoKycMutation = useUserStartVideoKyc({ invalidate: true })
  const addVideoKycDocumentMutation = useUserAddVideoKycDocument({
    invalidate: true,
  })

  const goTo = (step: SelfieKycStep, videoBlob?: Blob) =>
    setState((s) => ({
      ...s,
      step,
      ...(videoBlob !== undefined && { videoBlob }),
    }))

  const handleRequestCameraAccess = async (retryAttempt = 0) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error({
        title: t('selfie_kyc_error_not_supported'),
      })
      return
    }

    const MAX_RETRIES = 3
    const RETRY_DELAYS = [1000, 2000, 3000] // Progressive delays in ms

    setIsRequestingAccess(true)

    try {
      // Stop any existing stream first to avoid conflicts
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => {
          track.stop()
          track.enabled = false
        })
        activeStreamRef.current = null
      }

      // Small delay to ensure previous stream is fully released
      if (retryAttempt > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAYS[retryAttempt - 1] || 1000),
        )
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Prefer front camera
        },
        audio: true,
      })

      activeStreamRef.current = stream

      // Stop the stream immediately after getting permission
      stream.getTracks().forEach((track) => track.stop())
      activeStreamRef.current = null

      // Update permission states
      setHasCameraPermission(true)
      setHasMicrophonePermission(true)
      await checkAndUpdatePermissions()
    } catch (error: unknown) {
      console.error('Failed to get camera/microphone permission:', error)

      const domError = error instanceof DOMException ? error : null
      const errorMessageLower = domError?.message?.toLowerCase() || ''
      const isVideoInputError =
        errorMessageLower.includes('starting videoinput') ||
        errorMessageLower.includes('videoinput failed') ||
        domError?.name === 'NotReadableError' ||
        domError?.name === 'TrackStartError' ||
        domError?.name === 'AbortError'

      let errorMessage = t('selfie_kyc_error_permission_denied')
      let shouldRetry = false

      if (
        domError?.name === 'NotAllowedError' ||
        domError?.name === 'PermissionDeniedError'
      ) {
        shouldRetry = false
      } else if (
        domError?.name === 'NotFoundError' ||
        domError?.name === 'DevicesNotFoundError'
      ) {
        errorMessage = t('selfie_kyc_error_device_not_found')
        shouldRetry = false
      } else if (isVideoInputError) {
        errorMessage = t('selfie_kyc_error_device_in_use')
        shouldRetry = retryAttempt < MAX_RETRIES
      } else if (
        domError?.name === 'OverconstrainedError' ||
        domError?.name === 'ConstraintNotSatisfiedError'
      ) {
        errorMessage = t('selfie_kyc_error_constraints_not_satisfied')
        shouldRetry = false
      } else {
        shouldRetry = retryAttempt < 1
      }

      if (shouldRetry) {
        // Retry automatically with progressive delay
        setTimeout(() => {
          handleRequestCameraAccess(retryAttempt + 1)
        }, RETRY_DELAYS[retryAttempt] || 2000)
      } else {
        // Show error only if not retrying
        toast.error({
          title: errorMessage,
        })
      }

      await checkAndUpdatePermissions()
    } finally {
      setIsRequestingAccess(false)
    }
  }

  const handleStartNext = () => {
    if (hasCameraPermission && hasMicrophonePermission) {
      goTo(SelfieKycStep.Recording)
    }
  }

  const handleSubmitVideo = async () => {
    setIsLoading(true)

    if (!state.videoBlob) {
      toast.error({
        title: 'No video available',
      })
      setIsLoading(false)
      return
    }

    try {
      // Step 1: Start video KYC session
      const startResult = await startVideoKycMutation.mutateAsync({
        firstName: props.firstName,
        lastName: props.lastName,
        nationalID: props.nationalCode,
        birthdate: {
          year: Number(props.year),
          month: Number(props.month),
          day: Number(props.day),
        },
      })
      const workflowID = startResult.data?.workflowID
      const uploadLink = startResult.data?.uploadLink

      if (!workflowID || !uploadLink) {
        toast.error({
          title: 'Failed to get workflow ID or upload link',
        })
        setIsLoading(false)
        return
      }

      // Step 2: Upload video document using uploadLink
      const formData = new FormData()

      if (startResult.data?.formData) {
        Object.entries(startResult.data.formData).forEach(([key, value]) => {
          formData.append(key, value)
        })
      }
      formData.append('file', state.videoBlob, 'kyc-video.webm')

      try {
        await fetch(uploadLink, {
          method: 'POST',
          body: formData,
        })
      } catch (error) {
        console.log('🚀 ~ handleSubmitVideo ~ error:', error)
        toast.error({
          title: 'Upload failed',
        })
        setIsLoading(false)
        return
      }

      // Step 3: Add video KYC document
      try {
        await addVideoKycDocumentMutation.mutateAsync({
          workflowID: workflowID,
          name: startResult.data.name ?? 'kyc-video.webm',
          contentType: 'video/mp4',
        })

        // Step 4: Navigate to success
        window.location.href = `/${language}/auth/quick-kyc?mobileOrEmail=${props.mobileOrEmail}&confirmed=true`
      } catch (error) {
        console.log('🚀 ~ handleSubmitVideo ~ error:', error)
        toast.error({
          title: 'Failed to add video KYC document',
        })
        setIsLoading(false)
        return
      }
    } catch (error) {
      setIsLoading(false)
      console.log('🚀 ~ handleSubmitVideo ~ error:', error)
      toast.error({
        title: 'Failed to submit video',
      })
    }
  }

  return (
    <div className='fixed inset-0 sm:relative bottom-0 left-0 right-0 max-w-[480px] mx-auto'>
      {state.step === SelfieKycStep.Start && (
        <SelfieKycStart
          onNext={handleStartNext}
          onRequestCameraAccess={handleRequestCameraAccess}
          hasCameraPermission={hasCameraPermission && hasMicrophonePermission}
          isRequestingAccess={isRequestingAccess}
        />
      )}
      {state.step === SelfieKycStep.Recording && (
        <SelfieKycRecording
          onStop={() => goTo(SelfieKycStep.Confirm, state.videoBlob)}
          onTimeout={() => goTo(SelfieKycStep.Confirm, state.videoBlob)}
          onRecorded={(blob) => {
            // First, set the original blob immediately so video shows up
            setState((s) => ({ ...s, videoBlob: blob }))

            // Then compress in background
            compressVideo(blob).then((compressedBlob) => {
              setState((s) => ({ ...s, videoBlob: compressedBlob }))
            })
          }}
          dynamicTextProps={props}
        />
      )}
      {state.step === SelfieKycStep.Confirm && (
        <SelfieKycConfirm
          onSubmit={handleSubmitVideo}
          onBack={() => goTo(SelfieKycStep.Recording, state.videoBlob)}
          videoBlob={state.videoBlob}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}
